import React from "react"
import { graphql } from "gatsby"

import {
  Link,
  useTranslation,
  Trans,
} from "@zestia/gatsby-plugin-react-i18next"

import MainLayout from "@/layouts/MainLayout"

import CenteredHero from "@/components/CenteredHero"
import IntegrationHubCards from "@/components/IntegrationHubCards"
import IntegrationHubCard from "@/components/IntegrationHubCard"
import SplitPane from "@/components/SplitPane"
import QuoteLarge from "@/components/Quote"
import Accordion from "@/components/Accordion"
import AccordionItem from "@/components/AccordionItem"
import TryFreeCallToAction from "@/components/TryFreeCallToAction"

import IntegrationsWheelVideo from "@/videos/integrations-wheel.mp4"
import ContactHistoryVideo from "@/videos/contact-history.mp4"
import OpportunityMilestoneVideo from "@/videos/opportunity-milestone.mp4"

import SharedHead from "@/components/SharedHead"
import useHeadTranslation from "@/hooks/use-head-translation"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("microsoft-integrations.meta-title")}
      description={t("microsoft-integrations.meta-description")}
    />
  )
}

const Microsoft365IntegrationsCRM = ({}) => {
  const { t } = useTranslation()

  return (
    <MainLayout
      trackingData={{
        content_category: "integrations",
      }}
    >
      <CenteredHero
        centered="true"
        title={t("microsoft-integrations.header.title")}
        text={t("microsoft-integrations.header.body")}
        showButton="true"
        buttonText={t("microsoft-integrations.header.button")}
        buttonClass="btn btn--lg "
        backgroundClass="hero bg-grey-1 pt-16 pb-24"
      />

      <IntegrationHubCards className="integration-hub-cards--3">
        <IntegrationHubCard
          image={{
            src: "outlook.svg",
            alt: t("microsoft-integrations.cards.outlook-inbox.logo"),
          }}
          title={t("microsoft-integrations.cards.outlook-inbox.title")}
          description={t("microsoft-integrations.cards.outlook-inbox.body")}
          link="/support/integrations/outlook-add-in/"
          linkText={t("microsoft-integrations.cards.outlook-inbox.link")}
        />
        <IntegrationHubCard
          image={{
            src: "outlook.svg",
            alt: t("microsoft-integrations.cards.outlook-inbox.logo"),
          }}
          title={t("microsoft-integrations.cards.outlook-events.title")}
          description={t("microsoft-integrations.cards.outlook-events.body")}
          link="/support/integrations/outlook-calendar-integration/"
          linkText={t("microsoft-integrations.cards.outlook-events.link")}
        />
        <IntegrationHubCard
          image={{
            src: "microsoft.svg",
            alt: t("microsoft-integrations.cards.365.logo"),
          }}
          title={t("microsoft-integrations.cards.365.title")}
          description={t("microsoft-integrations.cards.365.body")}
          link="/support/integrations/microsoft-365-single-sign-on/"
          linkText={t("microsoft-integrations.cards.365.link")}
        />
      </IntegrationHubCards>

      <QuoteLarge
        quote={t("microsoft-integrations.quote.body")}
        cite={{
          name: "Pete Fullard",
          role_company: t("microsoft-integrations.quote.role"),
        }}
      />

      <SplitPane
        assetAlignment="right"
        title={t("microsoft-integrations.ecosystem.title")}
        text={t("microsoft-integrations.ecosystem.body")}
        video={IntegrationsWheelVideo}
        link={{
          url: "/integrations/",
          text: t("microsoft-integrations.ecosystem.link"),
        }}
      />

      <SplitPane
        assetAlignment="left"
        title={t("microsoft-integrations.manage-contacts.title")}
        text={t("microsoft-integrations.manage-contacts.body")}
        video={ContactHistoryVideo}
        link={{
          url: "/features/contact-management-software/",
          text: t("microsoft-integrations.manage-contacts.link"),
        }}
      />

      <SplitPane
        assetAlignment="right"
        title={t("microsoft-integrations.opportunities.title")}
        text={t("microsoft-integrations.opportunities.body")}
        video={OpportunityMilestoneVideo}
        link={{
          url: "/features/sales-pipeline/",
          text: t("microsoft-integrations.opportunities.link"),
        }}
      />

      <Accordion
        title={t("microsoft-integrations.microsoft-accordion.title")}
        description={t(
          "microsoft-integrations.microsoft-accordion.description",
        )}
      >
        <AccordionItem
          title={t("microsoft-integrations.microsoft-accordion.itemOne.title")}
        >
          <p>
            {t(
              "microsoft-integrations.microsoft-accordion.itemOne.firstParagraph",
            )}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t("microsoft-integrations.microsoft-accordion.itemTwo.title")}
        >
          <p>
            {t(
              "microsoft-integrations.microsoft-accordion.itemTwo.firstParagraph",
            )}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "microsoft-integrations.microsoft-accordion.itemThree.title",
          )}
        >
          <p>
            {t(
              "microsoft-integrations.microsoft-accordion.itemThree.firstParagraph",
            )}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t("microsoft-integrations.microsoft-accordion.itemFour.title")}
        >
          <p>
            {t(
              "microsoft-integrations.microsoft-accordion.itemFour.firstParagraph",
            )}
          </p>
          <p>
            {t(
              "microsoft-integrations.microsoft-accordion.itemFour.secondParagraph",
            )}
          </p>
        </AccordionItem>
      </Accordion>

      <SplitPane
        assetAlignment="stacked"
        title={t("microsoft-integrations.free-crm.title")}
      >
        <p className="mb-8 p--lg">
          {t("microsoft-integrations.free-crm.firstParagraph")}
        </p>
        <p className="mb-8 p--lg">
          <Trans
            i18nKey="microsoft-integrations.free-crm.secondParagraph"
            components={{
              PaidPlans: (
                <Link className="text-link text-link--bare" to="/signup/" />
              ),
            }}
          />
        </p>
      </SplitPane>

      <TryFreeCallToAction
        title={t("microsoft-integrations.cta.title")}
        description={t("microsoft-integrations.cta.description")}
        showFooter={false}
      />
    </MainLayout>
  )
}

export const query = graphql`
  query {
    ...Locales
    allFile(filter: { sourceInstanceName: { eq: "images" } }) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`

export default Microsoft365IntegrationsCRM
